import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, IconButton, Slide, Paper, Typography, TextField, CircularProgress } from '@material-ui/core';
import { Send, Close } from '@material-ui/icons';

import { AIChatBot } from '../../API/agent';
import {
  getIndustryName,
  getIssuesFromBrandPersona,
  getStateByCode,
  getCountryByCode,
  getEthnicityByCode,
  getGenderByCode,
  getEducationByCode,
  getMaritalStatusByCode,
  makeURL,
} from '../../helperFunctions';

const colorPallete = {
  brand: '#19989B',
  buyer: '#AB8628',
  competitive: '#19989B',
};

const useStyles = makeStyles(theme => ({
  chatWindow: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '90%',
    borderRadius: '16px',
    maxWidth: '500px',
    boxShadow: '1px 1px 3px 0px #737272',
    zIndex: 1000,
  },
  chatBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '80vh',
    border: props => `4px solid ${colorPallete[props.type]}`,
    borderRadius: '14px',
  },
  chatHeader: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: props => colorPallete[props.type],
    position: 'relative',
  },
  avatarContainer: {
    width: 85,
    height: 56,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    width: '85%',
    height: '60%',
    borderRadius: '5px',
  },
  closeChatButton: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: '0.8',
    transition: '0.3s ease-in-out',
    color: 'white',
    '&:hover': {
      opacity: '1',
    },
  },
  closeIcon: {
    fontSize: '30px',
  },
  chatContent: {
    height: '-webkit-fill-available',
    maxHeight: '-webkit-fill-available',
    overflowY: 'auto',
    borderLeft: props => `8px solid ${colorPallete[props.type]}`,
    borderRight: props => `8px solid ${colorPallete[props.type]}`,
    padding: '8px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: props => colorPallete[props.type],
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxWidth: '85%',
    boxShadow: '2px 2px 4px lightgrey',
  },
  systemMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#f5f5f5',
    padding: '4px 8px',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxWidth: '85%',
    boxShadow: '2px 2px 4px lightgrey',
  },
  chatFooter: {
    backgroundColor: props => colorPallete[props.type],
    padding: '6px 12px',
    display: 'flex',
    width: '100%',
  },
  chatForm: {
    backgroundColor: '#fff',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '4px',
    borderRadius: '8px',
  },
  chatInput: {
    border: 'none',
    outline: 'none',
    width: '100%',
  },
  sendButton: {
    minWidth: 40,
    minHeight: 40,
    padding: '2px',
  },
}));

const ChatBot = ({ pickedPersona, chatOpened, setChatOpened, type }) => {
  const classes = useStyles({ type });

  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const parseToMessage = value => (value ? value : 'unknown');

  const isBrandPersona = type === 'brand';
  const isCompetitive = type === 'competitive';

  useEffect(() => {
    setMessages([
      {
        role: 'system',
        content: isCompetitive
          ? `
              You are the world famous brand ${parseToMessage(pickedPersona.name)}, 
              to be exact you are the ${parseToMessage(pickedPersona.name)} brand persona.
              Gather all your knowledge about the ${parseToMessage(pickedPersona.name)} brand (industry, values, interests, inspiration, goals)
              and make sure you understand it as you are that brand persona.
            `
          : `
              You are ${parseToMessage(pickedPersona.Name)}, 
              a ${
                isBrandPersona ? 'brand' : 'buyer'
              } persona representing a young, sociable, and entertainment-loving individual
              ${
                isBrandPersona ? ` from the ${parseToMessage(getIndustryName(pickedPersona.Industry))} industry.` : '.'
              } 

              Your values include ${parseToMessage(pickedPersona.Value)}, 
              and you aim for ${parseToMessage(pickedPersona.Goal)}.

              You are passionate about addressing ${parseToMessage(pickedPersona.Problem)} 
              and are inspired by ${parseToMessage(pickedPersona.Influencers)}.

              Your favorite brand is ${parseToMessage(pickedPersona.Brand)}.

              Scaling from 0 to 100 in parentheses (100 being very strong opinion, 0 being none at all),
              you have these opinions on social issues: ${getIssuesFromBrandPersona(pickedPersona, type).join(', ')}.

              You live in ${parseToMessage(getCountryByCode(pickedPersona.country))}, 
              ${pickedPersona.State ? parseToMessage(getStateByCode(pickedPersona.State)) : ''}, 
              and belong to the ${parseToMessage(getEthnicityByCode(pickedPersona.Ethnicity))} ethnicity.
              
              You are ${parseToMessage(pickedPersona.Age)} years old, 
              ${parseToMessage(getGenderByCode(pickedPersona.Gender))} gender, 
              with a ${parseToMessage(getEducationByCode(pickedPersona.Education))}, 
              and are currently ${parseToMessage(getMaritalStatusByCode(pickedPersona.Marital_status))}.
            `,
      },
    ]);
  }, [pickedPersona]);

  const typeMessage = messageContent => {
    return new Promise(resolve => {
      let typedMessage = '';
      let index = 0;
      const typingInterval = setInterval(() => {
        typedMessage += messageContent[index++];
        setMessages(prevMessages => [
          ...prevMessages.slice(0, -1), // Remove the previous message (typing indicator)
          { role: 'assistant', content: typedMessage },
        ]);
        if (index === messageContent.length) {
          clearInterval(typingInterval);
          setIsTyping(false);
          resolve();
        }
      }, 25);
    });
  };

  const chatData = async userMessage => {
    AIChatBot([...messages, { role: 'user', content: userMessage }])
      .then(text => {
        setIsTyping(false);
        typeMessage(text);
      })
      .catch(err => {
        setIsTyping(false);
        showToastWarn();
      });
  };

  const showToastWarn = () => {
    toast.warn('Something went wrong while connecting to the chat assistant.', {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  const handleSendMessage = messageContent => {
    setMessages(prevMessages => [
      ...prevMessages,
      { role: 'user', content: messageContent },
      { role: 'assistant', content: 'Typing...' },
    ]);
    chatData(messageContent);
    setIsTyping(true);
  };

  if (!chatOpened) return null;

  return (
    <Slide direction="up" in={chatOpened} mountOnEnter unmountOnExit>
      <Paper className={classes.chatWindow}>
        <Box className={classes.chatBox}>
          <Box className={classes.chatHeader}>
            <Box className={classes.avatarContainer}>
              <img
                src={makeURL('/images/logos/BSV_light_no_background_Simple.png')}
                alt="Brand Social Value"
                className={classes.avatar}
              />
            </Box>
            <Box>
              <Typography style={{ color: '#fff' }}>{isCompetitive ? pickedPersona.name : pickedPersona.Name}</Typography>
            </Box>
            <IconButton onClick={() => setChatOpened(false)} className={classes.closeChatButton}>
              <Close className={classes.closeIcon} />
            </IconButton>
          </Box>
          <Box className={classes.chatContent}>
            {messages.map((message, index) => {
              if (message.role === 'system') return null;

              return (
                <Box key={index} className={message.role === 'user' ? classes.userMessage : classes.systemMessage}>
                  {message.role !== 'system' && (
                    <>
                      {message.role === 'assistant' && (
                        <Typography variant="body2" fontWeight="bold">
                          {message.role}
                        </Typography>
                      )}
                      <Box display="flex" alignItems="center" justifyContent="center">
                        {message.content === 'Typing...' ? (
                          <CircularProgress style={{ marginTop: '7px', width: '25px', height: '25px', color: 'black' }} />
                        ) : (
                          <Typography variant="body2">{message.content}</Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
          <Box className={classes.chatFooter}>
            <form
              className={classes.chatForm}
              onSubmit={e => {
                e.preventDefault();
                if (inputValue.trim() !== '') {
                  handleSendMessage(inputValue);
                  setInputValue('');
                }
              }}>
              <TextField
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Enter Your Message"
                variant="outlined"
                size="small"
                className={classes.chatInput}
              />
              <Button type="submit" className={classes.sendButton} disabled={isTyping}>
                <Send />
              </Button>
            </form>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default ChatBot;
